import { formatDateTime } from "../../../../lib/datetime/displayDateTime";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { alpha } from "@mui/material/styles";
import {
  AccountTree,
  Bolt,
  Category,
  Chat,
  Comment,
  Email,
  Delete,
  Edit,
  Engineering,
  ExpandMore,
  Inbox,
  OpenInNew,
  PersonAdd,
  ReportProblem,
  SwapHoriz,
  WarningAmber,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { resolvePriorityColor } from "../../../../assets/theme/geminiPalette";
import type { Incident } from "../../../../types/incident";
import type { DetectedAnomalyEvent } from "../../../../types/anomalyEvent";
import type { IncidentFolderId } from "../../lib/constants";
import { getData } from "../../../../api/api";
import { buildOriginalEmailDocument } from "../../../../lib/incident/originalEmailPreview";
import {
  getIncidentPriorityLabel,
  INCIDENT_PRIORITY_OPTIONS,
  normalizeIncidentPriority,
  toIncidentPriorityEnum,
} from "../../lib/constants";
import { useAnomalyGuideClassification } from "../../lib/useAnomalyGuideClassification";
import { IconAutocomplete } from "../../../../components/forms";
import { incidentService } from "../../../../services/incidentService";
import {
  IncidentPriorityChip,
  IncidentStatusChip,
  resolveDetectedAnomalyTone,
} from "../../lib/incidentChips";
import { persistIncidentsWorkspaceSelection } from "../../../../lib/incident/workspaceSelection";
import { useIncidentCustomerName } from "../../../../lib/incident/useIncidentCustomerName";
import { isEmailNotificationAvailable } from "../../../../lib/incident/emailNotification";
import { isWhatsAppNotificationAvailable } from "../../../../lib/incident/whatsappNotification";
import {
  pageButtonOutlinedPrimaryProps,
  pageButtonPrimaryProps,
} from "../../../../lib/ui/pageButtons";
import { useEffect, useMemo, useRef, useState } from "react";
import IncidentComments, { type IncidentCommentsHandle } from "./IncidentComments";

const unlabeledValue = (value?: string | null) => {
  const text = value?.trim();
  return text ? text : "-";
};

type IncidentReadingPaneProps = {
  incident: Incident | null;
  selectedFolder: IncidentFolderId;
  onAssign: (incident: Incident) => void;
  onCommentAdded: () => void;
  onStatus: (incident: Incident) => void;
  onEdit: (incident: Incident) => void;
  onDelete: (incident: Incident) => void;
  onSendWhatsApp: (incident: Incident) => void;
  sendingWhatsApp?: boolean;
  onSendEmail: (incident: Incident) => void;
  sendingEmail?: boolean;
};

export default function IncidentReadingPane({
  incident,
  selectedFolder,
  onAssign,
  onCommentAdded,
  onStatus,
  onEdit,
  onDelete,
  onSendWhatsApp,
  sendingWhatsApp = false,
  onSendEmail,
  sendingEmail = false,
}: IncidentReadingPaneProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const commentsRef = useRef<IncidentCommentsHandle>(null);
  const [actionMenuAnchor, setActionMenuAnchor] = useState<null | HTMLElement>(null);
  const customerName = useIncidentCustomerName(incident);

  useEffect(() => {
    setActionMenuAnchor(null);
  }, [incident?.id]);

  if (!incident) {
    return (
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: (theme) => alpha(theme.palette.divider, 0.04),
          overflow: "hidden",
        }}
      >
        <Box textAlign="center" sx={{ px: 3 }}>
          <Inbox sx={{ fontSize: 48, color: "text.disabled", mb: 1.5 }} />
          <Typography variant="h6" color="text.secondary" fontWeight={600} gutterBottom>
            {t("incidents.selectIncident")}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 320, mx: "auto" }}>
            {t("incidents.selectIncidentHint")}
          </Typography>
        </Box>
      </Box>
    );
  }

  const sourceSystemLabel = [
    incident.sourceSystem?.name?.trim(),
    incident.sourceSystem?.id?.trim() || incident.sourceSystemId?.trim(),
  ]
    .filter(Boolean)
    .join(" ");
  const detectedAtText = incident.detectedAt ? formatDateTime(incident.detectedAt, "medium") : "-";
  const canSendWhatsApp = isWhatsAppNotificationAvailable(incident);
  const canSendEmail = isEmailNotificationAvailable(incident);

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        height: "100%",
        maxHeight: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        bgcolor: "background.paper",
      }}
    >
      <Box
        sx={{
          px: 2.5,
          py: 1.75,
          borderBottom: 1,
          borderColor: "divider",
          flexShrink: 0,
        }}
      >
        <Stack spacing={1.25} sx={{ mb: 1.5 }}>
          <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase" }}>
                {t("incidents.customer")}
              </Typography>
              <Typography variant="body2" fontWeight={700} noWrap title={customerName || undefined}>
                {customerName?.trim() || "—"}
              </Typography>
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase" }}>
                {t("incidents.fromMail")}
              </Typography>
              <Typography variant="body2" fontWeight={600} sx={{ wordBreak: "break-all" }}>
                {incident.fromMail?.trim() || "—"}
              </Typography>
            </Box>
          </Stack>
        </Stack>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack direction="row" alignItems="center" spacing={0.75} flexWrap="wrap" useFlexGap>
              <IncidentPriorityChip incident={incident} />
              <IncidentStatusChip status={incident.status} />
            </Stack>
          </Box>

          <Stack alignItems="flex-end" spacing={0.5} sx={{ flexShrink: 0 }}>
            <Stack direction="row" alignItems="center" spacing={0.75} flexWrap="wrap" useFlexGap justifyContent="flex-end">
              <Engineering sx={{ fontSize: 18 }} />
              <Typography variant="body2" fontWeight={700}>
                {unlabeledValue(incident.assignedTo)}
              </Typography>
              <Typography variant="body2" fontWeight={700}>
                {unlabeledValue(incident.assignedGroup)}
              </Typography>
            </Stack>
            <Typography variant="caption" color="text.secondary">
              {formatDateTime(incident.createdDate, "medium")}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {unlabeledValue(incident.createdBy)}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontFamily: "ui-monospace, monospace" }}
            >
              {incident.id}
            </Typography>
          </Stack>
        </Stack>

        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={0.75}
          sx={{ mt: 1.5, width: 1 }}
        >
          <Button
            {...pageButtonOutlinedPrimaryProps}
            size="small"
            startIcon={<OpenInNew />}
            onClick={() => {
              persistIncidentsWorkspaceSelection({
                incidentId: incident.id,
                folder: selectedFolder,
              });
              navigate(`/incidents/${incident.id}`);
            }}
            sx={{ textTransform: "none", fontWeight: 600, minHeight: { xs: 44, md: 36 } }}
          >
            {t("incidents.openFullDetail")}
          </Button>
          <Box sx={{ ml: "auto", flexShrink: 0 }}>
          <Button
            {...pageButtonPrimaryProps}
            size="small"
            startIcon={<Bolt />}
            endIcon={<ExpandMore />}
            onClick={(event) => setActionMenuAnchor(event.currentTarget)}
            sx={{ textTransform: "none", fontWeight: 600, minHeight: { xs: 44, md: 36 } }}
          >
            {t("incidents.actions")}
          </Button>
          <Menu
            anchorEl={actionMenuAnchor}
            open={Boolean(actionMenuAnchor)}
            onClose={() => setActionMenuAnchor(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            sx={{ "& .MuiMenuItem-root": { minHeight: 48 } }}
          >
            <MenuItem
              onClick={() => {
                setActionMenuAnchor(null);
                onAssign(incident);
              }}
            >
              <ListItemIcon>
                <PersonAdd fontSize="small" />
              </ListItemIcon>
              <ListItemText primary={t("incidents.assign")} />
            </MenuItem>
            <MenuItem
              onClick={() => {
                setActionMenuAnchor(null);
                requestAnimationFrame(() => commentsRef.current?.startComment());
              }}
            >
              <ListItemIcon>
                <Comment fontSize="small" />
              </ListItemIcon>
              <ListItemText primary={t("incidents.comment")} />
            </MenuItem>
            <MenuItem
              onClick={() => {
                setActionMenuAnchor(null);
                onStatus(incident);
              }}
            >
              <ListItemIcon>
                <SwapHoriz fontSize="small" />
              </ListItemIcon>
              <ListItemText primary={t("incidents.changeStatus")} />
            </MenuItem>
            <Divider />
            <MenuItem
              disabled={!canSendEmail || sendingEmail}
              onClick={() => {
                setActionMenuAnchor(null);
                onSendEmail(incident);
              }}
            >
              <ListItemIcon>
                <Email fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary={t("incidents.sendEmail")}
                secondary={!canSendEmail ? t("incidents.sendEmailUnavailable") : undefined}
              />
            </MenuItem>
            <MenuItem
              disabled={!canSendWhatsApp || sendingWhatsApp}
              onClick={() => {
                setActionMenuAnchor(null);
                onSendWhatsApp(incident);
              }}
            >
              <ListItemIcon>
                <Chat fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary={t("incidents.sendWhatsApp")}
                secondary={!canSendWhatsApp ? t("incidents.sendWhatsAppUnavailable") : undefined}
              />
            </MenuItem>
            <Divider />
            <MenuItem
              onClick={() => {
                setActionMenuAnchor(null);
                onEdit(incident);
              }}
            >
              <ListItemIcon>
                <Edit fontSize="small" />
              </ListItemIcon>
              <ListItemText primary={t("incidents.edit")} />
            </MenuItem>
            <MenuItem
              onClick={() => {
                setActionMenuAnchor(null);
                onDelete(incident);
              }}
              sx={{ color: "error.main" }}
            >
              <ListItemIcon sx={{ color: "error.main" }}>
                <Delete fontSize="small" />
              </ListItemIcon>
              <ListItemText primary={t("incidents.delete")} />
            </MenuItem>
          </Menu>
          </Box>
        </Stack>
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", overflowX: "hidden", pb: { xs: 10, md: 2 } }}>
        <IncidentClassificationStrip incident={incident} onSaved={onCommentAdded} />
        <Divider />

        <Box sx={{ px: 2.5, py: 2 }}>
          {sourceSystemLabel ? (
            <Typography
              variant="body2"
              component="div"
              sx={{ fontWeight: 800, lineHeight: 1.4, mb: 0.25 }}
            >
              {sourceSystemLabel}
            </Typography>
          ) : null}
          <Typography
            component="h2"
            variant="body2"
            sx={{
              fontWeight: 400,
              fontSize: (theme) => `calc(${theme.typography.body2.fontSize} + 2pt)`,
              lineHeight: 1.45,
              mb: 1.5,
            }}
          >
            {incident.title?.trim() || t("incidents.untitled")}
            <Box component="span" sx={{ color: "text.secondary" }}>
              {" | "}
              {detectedAtText}
            </Box>
          </Typography>
          <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700, letterSpacing: "0.06em" }}>
            {t("incidents.description")}
          </Typography>
          <Typography variant="body2" sx={{ mt: 1, lineHeight: 1.65, whiteSpace: "pre-wrap" }}>
            {incident.description?.trim() || "—"}
          </Typography>
          <OriginalEmailBody openSearchId={incident.openSearchId} />
        </Box>

        <IncidentComments ref={commentsRef} incident={incident} onAdded={onCommentAdded} />
      </Box>
    </Box>
  );
}

const SEVERITY_RANK: Record<string, number> = {
  CRISIS: 6,
  CRITICAL: 5,
  Critical: 5,
  HIGH: 4,
  High: 4,
  MEDIUM: 3,
  Medium: 3,
  LOW: 2,
  Low: 2,
  Informational: 1,
  INFORMATIONAL: 1,
};

function severityRank(value?: string | null): number {
  if (!value) return 0;
  return SEVERITY_RANK[value] ?? SEVERITY_RANK[value.toUpperCase()] ?? 0;
}

function metadataSeverity(metadata: Incident["metadata"]): string | undefined {
  if (!metadata || typeof metadata !== "object") return undefined;
  const raw = (metadata as Record<string, unknown>).severity;
  return typeof raw === "string" && raw.trim() ? raw.trim() : undefined;
}

function resolveIncidentSeverity(incident: Incident): string | undefined {
  const fromAnomalies = (incident.detectedAnomalyEvents ?? [])
    .map((event: DetectedAnomalyEvent) => event.definition?.defaultSeverity)
    .filter((value): value is NonNullable<typeof value> => Boolean(value?.trim()));

  if (fromAnomalies.length > 0) {
    return [...fromAnomalies].sort((a, b) => severityRank(b) - severityRank(a))[0];
  }

  const fromMetadata = metadataSeverity(incident.metadata);
  if (fromMetadata) return fromMetadata;

  return incident.priority || undefined;
}

function ClassificationValue({ value }: { value?: string | null }) {
  const text = value?.trim() || "—";
  return (
    <Typography variant="body1" title={text === "—" ? undefined : text}>
      {text}
    </Typography>
  );
}

function ClassificationCard({
  label,
  icon,
  children,
  accent,
  editable = false,
  editing = false,
  onEdit,
}: {
  label: string;
  icon: React.ReactNode;
  children: React.ReactNode;
  accent?: string;
  editable?: boolean;
  editing?: boolean;
  onEdit?: () => void;
}) {
  const { t } = useTranslation();
  const canOpen = editable && !editing;

  return (
    <Paper
      variant="outlined"
      role={canOpen ? "button" : undefined}
      tabIndex={canOpen ? 0 : undefined}
      aria-label={canOpen ? t("incidents.edit") : undefined}
      onClick={canOpen ? onEdit : undefined}
      onKeyDown={
        canOpen
          ? (event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                onEdit?.();
              }
            }
          : undefined
      }
      sx={{
        p: 1.5,
        borderRadius: 2,
        height: "100%",
        width: "100%",
        textAlign: "left",
        font: "inherit",
        color: "inherit",
        cursor: canOpen ? "pointer" : "default",
        borderColor: (theme) => (accent ? alpha(accent, theme.palette.mode === "dark" ? 0.55 : 0.4) : "divider"),
        bgcolor: (theme) =>
          accent
            ? alpha(accent, theme.palette.mode === "dark" ? 0.22 : 0.14)
            : alpha(theme.palette.primary.main, 0.06),
      }}
    >
      <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.75 }}>
        <Box
          sx={{
            display: "inline-flex",
            color: accent || "primary.main",
            "& .MuiSvgIcon-root": { fontSize: 18 },
          }}
        >
          {icon}
        </Box>
        <Typography
          variant="caption"
          fontWeight={800}
          letterSpacing="0.08em"
          sx={{ textTransform: "uppercase", color: accent || "text.secondary", flex: 1 }}
        >
          {label}
        </Typography>
        {canOpen ? <Edit sx={{ fontSize: 18, color: "text.secondary" }} /> : null}
      </Stack>
      {editing ? (
        <Box onClick={(event) => event.stopPropagation()} onKeyDown={(event) => event.stopPropagation()}>
          {children}
        </Box>
      ) : (
        children
      )}
    </Paper>
  );
}

function notify(color: "success" | "error", title: string, content: string) {
  window.dispatchEvent(
    new CustomEvent("showGlobalNotification", {
      detail: { color, icon: color, title, content },
    }),
  );
}

type EditableClassificationField = "classification" | "severity";

function IncidentClassificationStrip({
  incident,
  onSaved,
}: {
  incident: Incident;
  onSaved: () => void;
}) {
  const [editingField, setEditingField] = useState<EditableClassificationField | null>(null);

  useEffect(() => {
    setEditingField(null);
  }, [incident.id]);

  return (
    <Box sx={{ px: 2.5, pt: 2, pb: 0 }}>
      <ClassificationGrid
        incident={incident}
        editingField={editingField}
        onEdit={setEditingField}
        onCancel={() => setEditingField(null)}
        onSaved={() => {
          setEditingField(null);
          onSaved();
        }}
      />
    </Box>
  );
}

function ClassificationGrid({
  incident,
  editingField,
  onEdit,
  onCancel,
  onSaved,
}: {
  incident: Incident;
  editingField: EditableClassificationField | null;
  onEdit: (field: EditableClassificationField) => void;
  onCancel: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const severity = resolveIncidentSeverity(incident);
  const detectedAnomaly = primaryDetectedAnomalyLabel(incident);
  const anomalyTone = resolveDetectedAnomalyTone(incident);
  const cell = { xs: 12, sm: 3 } as const;

  return (
    <Grid container spacing={1.5}>
      {editingField === "classification" ? (
        <ClassificationPairEditor incident={incident} cell={cell} onCancel={onCancel} onSaved={onSaved} />
      ) : (
        <>
          <Grid size={cell}>
            <ClassificationCard
              label={t("incidents.category")}
              icon={<Category />}
              editable
              onEdit={() => onEdit("classification")}
            >
              <ClassificationValue value={incident.category?.trim()} />
            </ClassificationCard>
          </Grid>
          <Grid size={cell}>
            <ClassificationCard
              label={t("incidents.subcategory")}
              icon={<AccountTree />}
              editable
              onEdit={() => onEdit("classification")}
            >
              <ClassificationValue value={incident.subcategory?.trim()} />
            </ClassificationCard>
          </Grid>
        </>
      )}
      <Grid size={cell}>
        <ClassificationCard
          label={t("incidents.severity")}
          icon={<ReportProblem />}
          accent={severity ? resolvePriorityColor(severity) : undefined}
          editable
          editing={editingField === "severity"}
          onEdit={() => onEdit("severity")}
        >
          {editingField === "severity" ? (
            <SeverityEditor incident={incident} displayedSeverity={severity} onCancel={onCancel} onSaved={onSaved} />
          ) : (
            <ClassificationValue value={severity ? getIncidentPriorityLabel(severity) : undefined} />
          )}
        </ClassificationCard>
      </Grid>
      <Grid size={cell}>
        <ClassificationCard
          label={t("incidents.detectedAnomaly")}
          icon={<WarningAmber />}
          accent={anomalyTone?.color}
        >
          <ClassificationValue value={detectedAnomaly} />
        </ClassificationCard>
      </Grid>
    </Grid>
  );
}

function FieldEditorActions({
  onCancel,
  onUpdate,
  disabled,
}: {
  onCancel: () => void;
  onUpdate: () => void;
  disabled: boolean;
}) {
  const { t } = useTranslation();
  return (
    <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 1 }}>
      <Button {...pageButtonOutlinedPrimaryProps} size="small" onClick={onCancel} disabled={disabled}>
        {t("common.cancel")}
      </Button>
      <Button {...pageButtonPrimaryProps} size="small" onClick={onUpdate} disabled={disabled}>
        {t("common.update")}
      </Button>
    </Stack>
  );
}

async function saveIncidentClassification(
  incident: Incident,
  patch: { category?: string; subcategory?: string; priority?: string; description?: string },
) {
  await incidentService.updateIncident({
    id: incident.id,
    title: incident.title,
    description: patch.description ?? incident.description ?? "",
    priority: toIncidentPriorityEnum(patch.priority ?? incident.priority),
    sourceSystemId: incident.sourceSystemId || incident.sourceSystem?.id || "",
    assignedTo: incident.assignedTo || undefined,
    assignedGroup: incident.assignedGroup || undefined,
    category: patch.category ?? incident.category ?? "",
    subcategory: patch.subcategory ?? incident.subcategory ?? "",
    tags: incident.tags ?? [],
    affectedUsers: incident.affectedUsers,
    sourceIps: incident.sourceIps,
    destinationIps: incident.destinationIps,
    metadata: {
      ...(typeof incident.metadata === "object" && incident.metadata ? incident.metadata : {}),
      ...(patch.priority ? { severity: patch.priority } : {}),
    },
    openSearchId: incident.openSearchId || "",
  });
}

function ClassificationPairEditor({
  incident,
  cell,
  onCancel,
  onSaved,
}: {
  incident: Incident;
  cell: { xs: number; sm: number };
  onCancel: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const [saving, setSaving] = useState(false);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const {
    category,
    subcategory,
    setCategory,
    setSubcategory,
    categoryOptions,
    subcategoryOptions,
    classificationGuidance,
    taxonomyReady,
    taxonomyLoading,
  } = useAnomalyGuideClassification(
    { category: incident.category, subcategory: incident.subcategory },
    incident.id,
  );

  const subcategoryMissing = !subcategory?.trim();
  const warning = !category?.trim()
    ? submitAttempted
      ? t("incidents.classificationRequired")
      : null
    : subcategoryMissing
      ? t("incidents.subcategoryRequired")
      : null;

  const handleUpdate = async () => {
    if (saving) return;
    if (!category?.trim() || subcategoryMissing) {
      setSubmitAttempted(true);
      return;
    }
    setSaving(true);
    try {
      const classificationChanged =
        category !== (incident.category ?? "").trim() ||
        (subcategory ?? "") !== (incident.subcategory ?? "").trim();
      await saveIncidentClassification(incident, {
        category,
        subcategory: subcategory ?? "",
        ...(taxonomyReady && classificationChanged && classificationGuidance != null
          ? { description: classificationGuidance }
          : {}),
      });
      notify("success", t("common.update"), t("incidents.updatedSuccess"));
      onSaved();
    } catch (error) {
      console.error("Failed to update incident classification:", error);
      notify("error", t("common.update"), t("incidents.updateFailed"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Grid size={cell}>
        <ClassificationCard label={t("incidents.category")} icon={<Category />} editing>
          <IconAutocomplete
            options={categoryOptions}
            value={category}
            onChange={(value) => {
              setCategory(value);
              setSubmitAttempted(false);
            }}
            placeholder={t("anomalies.selectCategory")}
            loading={taxonomyLoading}
            noOptionsText={
              taxonomyLoading
                ? t("anomalies.form.loadingSubcategories")
                : t("anomalies.form.noSubcategories")
            }
          />
        </ClassificationCard>
      </Grid>
      <Grid size={cell}>
        <ClassificationCard label={t("incidents.subcategory")} icon={<AccountTree />} editing>
          <IconAutocomplete
            options={subcategoryOptions}
            value={subcategory}
            onChange={(value) => {
              setSubcategory(value);
              setSubmitAttempted(false);
            }}
            placeholder={
              category ? t("anomalies.form.searchSubcategory") : t("anomalies.selectCategoryFirst")
            }
            disabled={!category}
            loading={taxonomyLoading}
            noOptionsText={
              !category
                ? t("anomalies.selectCategoryFirstLong")
                : taxonomyLoading
                  ? t("anomalies.form.loadingSubcategories")
                  : t("anomalies.form.noSubcategories")
            }
          />
          {warning ? (
            <Typography variant="caption" color="error" sx={{ display: "block", mt: 0.75 }}>
              {warning}
            </Typography>
          ) : null}
          <FieldEditorActions onCancel={onCancel} onUpdate={() => void handleUpdate()} disabled={saving} />
        </ClassificationCard>
      </Grid>
    </>
  );
}

function SeverityEditor({
  incident,
  displayedSeverity,
  onCancel,
  onSaved,
}: {
  incident: Incident;
  displayedSeverity?: string;
  onCancel: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const [saving, setSaving] = useState(false);
  const [priority, setPriority] = useState(
    normalizeIncidentPriority(displayedSeverity) ?? normalizeIncidentPriority(incident.priority),
  );

  const handleUpdate = async () => {
    if (saving || !priority) return;
    setSaving(true);
    try {
      await saveIncidentClassification(incident, { priority });
      notify("success", t("common.update"), t("incidents.updatedSuccess"));
      onSaved();
    } catch (error) {
      console.error("Failed to update incident severity:", error);
      notify("error", t("common.update"), t("incidents.updateFailed"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <IconAutocomplete
        options={INCIDENT_PRIORITY_OPTIONS}
        value={priority}
        onChange={setPriority}
        disableClearable
        placeholder={t("incidents.severity")}
      />
      <FieldEditorActions
        onCancel={onCancel}
        onUpdate={() => void handleUpdate()}
        disabled={saving || !priority}
      />
    </>
  );
}

function OriginalEmailBody({ openSearchId }: { openSearchId?: string | null }) {
  const { t } = useTranslation();
  const [bodyContent, setBodyContent] = useState("");
  const [contentType, setContentType] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const id = openSearchId?.trim();
    if (!id) {
      setBodyContent("");
      setContentType(null);
      setLoading(false);
      return;
    }

    let active = true;
    setLoading(true);
    setBodyContent("");
    void getData(`/opensearch/surelog-events/_doc/${id}`, {
      headers: { accept: "*/*" },
      skipGlobalLoading: true,
    })
      .then((data) => {
        if (!active) return;
        const body = data?._source?.body;
        setBodyContent(typeof body?.content === "string" ? body.content : "");
        setContentType(typeof body?.contentType === "string" ? body.contentType : null);
      })
      .catch((error) => {
        console.error("Failed to load original email content:", error);
        if (!active) return;
        setBodyContent("");
        setContentType(null);
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [openSearchId]);

  const srcDoc = useMemo(
    () => (bodyContent ? buildOriginalEmailDocument(bodyContent, contentType) : ""),
    [bodyContent, contentType],
  );

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700, letterSpacing: "0.06em" }}>
        {t("incidents.viewOriginalEmail")}
      </Typography>
      {loading ? (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {t("common.loading")}
        </Typography>
      ) : srcDoc ? (
        <Box
          sx={{
            mt: 1,
            height: 420,
            border: 1,
            borderColor: "divider",
            borderRadius: 1,
            overflow: "hidden",
            bgcolor: "#ffffff",
          }}
        >
          <iframe
            title={t("common.originalEmail")}
            srcDoc={srcDoc}
            sandbox=""
            referrerPolicy="no-referrer"
            style={{ display: "block", width: "100%", height: "100%", border: 0, background: "#ffffff" }}
          />
        </Box>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {t("incidents.noOriginalEmailContent")}
        </Typography>
      )}
    </Box>
  );
}

function primaryDetectedAnomalyLabel(incident: Incident): string {
  const events = incident.detectedAnomalyEvents ?? [];
  const event = events[0];
  if (!event) return "";

  return event.definition?.definition?.trim() || event.anomalyEventId?.trim() || "";
}
